/**
 * brandFont — the ONE font face used by BOTH ends of the design engine.
 *
 * REAL BRAND FACE (value-6, deterministic, never silently substitute):
 * HorseDeal's brand face is `sofiapro`. The design renderer uses licensed Sofia
 * Pro TrueType assets locally on BOTH ends: node-canvas registers the server copy
 * from render/fonts/, and the browser editor loads the mirrored /static/fonts/
 * copy through FontFace before first paint.
 *
 * Both ends register the same files under ONE stable internal family name,
 * BRAND_FONT_FAMILY. Existing design docs therefore keep using `social-brand`
 * while the actual face behind it is Sofia Pro. Because the editor measures text
 * against the exact same face the server registers, `preview == published` holds
 * at the GLYPH level — the whole point of one-engine-both-ends.
 *
 * PURE CJS, no node/browser-only requires — imported by the server renderer, the
 * client editor, and the default templates alike.
 *
 * @module render/brandFont
 */

// The single registered family name used by design docs. Keep this stable so
// persisted docs do not need a font-family migration when the underlying files
// change.
const BRAND_FONT_FAMILY = 'social-brand';

// family + weight -> file, resolved against render/fonts/ (server) and
// /static/fonts/ (client, via FontFace). Sofia Pro Regular (400) + Bold (700).
const FILES = [
    { family: BRAND_FONT_FAMILY, weight: 400, file: 'sofiapro-regular-webfont.ttf' },
    { family: BRAND_FONT_FAMILY, weight: 700, file: 'sofiapro-bold-webfont.ttf' },
];

const REGULAR_FILE = 'sofiapro-regular-webfont.ttf';
const BOLD_FILE = 'sofiapro-bold-webfont.ttf';

const FONT_FILE_MAP = {
    'DejaVuSans.ttf': REGULAR_FILE,
    'Poppins-Regular.ttf': REGULAR_FILE,
    'sofiapro-regular-webfont.woff': REGULAR_FILE,
    'sofiapro-regular-webfont.woff2': REGULAR_FILE,
    'DejaVuSans-Bold.ttf': BOLD_FILE,
    'Poppins-Bold.ttf': BOLD_FILE,
    'sofiapro-bold-webfont.woff': BOLD_FILE,
    'sofiapro-bold-webfont.woff2': BOLD_FILE,
};

const normalizeDesignFontFile = file => FONT_FILE_MAP[file] || file;

const normalizeDesignDocFonts = doc => {
    if (!doc || !Array.isArray(doc.fonts)) return doc;
    let changed = false;
    const fonts = doc.fonts.map(font => {
        if (!font || typeof font !== 'object') return font;
        const file = normalizeDesignFontFile(font.file);
        if (file === font.file) return font;
        changed = true;
        return { ...font, file };
    });
    return changed ? { ...doc, fonts } : doc;
};

export { BRAND_FONT_FAMILY, FILES, normalizeDesignFontFile, normalizeDesignDocFonts };
